import { IBankingDetails } from '@insio/shared-types';
/**
 * Banking validation utilities — format-only checks for SWIFT/BIC, Czech bank
 * codes and IBAN. None of these run checksum validation; they verify the
 * structural shape so a UI can surface inline format warnings.
 *
 * @module validation/banking
 */

/**
 * SWIFT / BIC: 4-letter bank code, 2-letter country, 2-alphanumeric location,
 * optional 3-alphanumeric branch (ISO 9362).
 */
export const SWIFT_PATTERN = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

/**
 * Czech bank code: exactly 4 digits (e.g. "0800", "0100").
 */
export const BANK_CODE_PATTERN = /^\d{4}$/;

/**
 * IBAN basic structure: 2-letter country code, 2-digit check digits,
 * 4–30 uppercase alphanumeric characters (ISO 13616, format only).
 */
export const IBAN_PATTERN = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4,30}$/;

/**
 * Normalize any account number for comparison: strip whitespace and upper-case.
 * Safe for Czech `[prefix-]number/bankCode` notation too — digits, `-` and `/` are unaffected.
 */
export function normalizeAccountNumber(value: string | null | undefined): string {
  return (value ?? '').replace(/\s/g, '').toUpperCase();
}

/** Normalize an IBAN for comparison: strip whitespace and upper-case. */
export function normalizeIban(value: string | null | undefined): string {
  return normalizeAccountNumber(value);
}

/** Returns true if the SWIFT/BIC value is empty or matches the ISO 9362 format. */
export function isValidSwift(value: string | null | undefined): boolean {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return true;
  return SWIFT_PATTERN.test(trimmed);
}

/** Returns true if the Czech bank code value is empty or 4 digits. */
export function isValidBankCode(value: string | null | undefined): boolean {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return true;
  return BANK_CODE_PATTERN.test(trimmed);
}

/** Returns true if the IBAN (after normalization) is empty or matches the format. */
export function isValidIban(value: string | null | undefined): boolean {
  const normalized = normalizeIban(value ?? '');
  if (!normalized) return true;
  return IBAN_PATTERN.test(normalized);
}

/** Returns true if any field in the banking details form has a non-empty trimmed value. */
export function hasAnyBankingField(formData: Partial<IBankingDetails>): boolean {
  return Object.values(formData).some(value => (value?.trim() ?? '') !== '');
}
